// Shared types for Grocery Planner application

// Common store names
export const STORES = ['all', 'K-Market', 'S-Market', 'Prisma', 'Citymarket', 'Alepa', 'Lidl', 'Tokmanni'] as const

export interface PriceHistory {
  date: string; // Date in YYYY-MM-DD format
  price: number; // Price in euros
}

export interface StorePrice {
  store: string; // Store name
  price: number; // Price in euros
  shelfNumber?: string; // Shelf number in this store (optional)
  storeName?: string; // Store-specific product name (e.g. "Pirkka kevytmaito")
  priceHistory?: PriceHistory[]; // Price history for this store (optional)
}

export interface Product {
  name: string; // Product name
  prices: StorePrice[]; // Prices in different stores
  category: string; // Product category
  isFavorite: boolean; // Is favorite product
  rotationDays?: number; // Days until product runs out (optional)
}

// Helper function to get price for a specific store
export function getProductPrice(product: Product, store: string): number {
  const storePrice = product.prices.find(p => p.store === store)
  if (storePrice) return storePrice.price
  
  // If not found in specific store, return price from 'all' stores
  const allStorePrice = product.prices.find(p => p.store === 'all')
  if (allStorePrice) return allStorePrice.price
  
  // If no 'all' store price, return first available price
  return product.prices.length > 0 ? product.prices[0].price : 0
}

// Helper function to get shelf number for a specific store
export function getProductShelfNumber(product: Product, store: string): string {
  const storePrice = product.prices.find(p => p.store === store)
  if (storePrice?.shelfNumber) return storePrice.shelfNumber
  
  // If not found in specific store, return shelf from 'all' stores
  const allStorePrice = product.prices.find(p => p.store === 'all')
  if (allStorePrice?.shelfNumber) return allStorePrice.shelfNumber
  
  // If no specific shelf info, return first available shelf number
  const priceWithShelf = product.prices.find(p => p.shelfNumber)
  return priceWithShelf?.shelfNumber || ''
}

// Helper function to get the best (lowest) price for a product
export function getBestPrice(product: Product): { price: number; store: string } {
  if (product.prices.length === 0) return { price: 0, store: 'Unknown' }
  
  const bestPrice = product.prices.reduce((best, current) => 
    current.price < best.price ? current : best
  )
  
  return { price: bestPrice.price, store: bestPrice.store }
}

// Helper function to check if product is available in store
export function isProductAvailableInStore(product: Product, store: string): boolean {
  return product.prices.some(p => p.store === store || p.store === 'all')
}

// Helper function to get store-specific product name
export function getProductStoreName(product: Product, store: string): string {
  const storePrice = product.prices.find(p => p.store === store)
  if (storePrice?.storeName) return storePrice.storeName
  
  // If not found in specific store, return store name from 'all' stores
  const allStorePrice = product.prices.find(p => p.store === 'all')
  if (allStorePrice?.storeName) return allStorePrice.storeName
  
  // If no store-specific name, return the general product name
  return product.name
}

export interface ShoppingListItem {
  name: string; // Product name
  quantity: number; // Quantity (can be decimal for weight-based items)
  quantityUnit?: 'pcs' | 'kg' | 'g' | 'l' | 'ml'; // Unit of measurement
  unitPrice?: number; // Unit price in euros (from selected store) - optional for legacy data
  store?: string; // Store where this price is from
}

export interface ShoppingList {
  name: string; // List name
  items: ShoppingListItem[]; // Products in the list
  createdAt?: string; // Creation date (ISO string)
  updatedAt?: string; // Last modified date (ISO string)
}

export interface User {
  id: string;
  email: string;
  name: string;
  picture?: string;
}

// API Response types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface ProductsData {
  products: Product[];
  lastModified: string;
}

export interface ShoppingListsData {
  lists: ShoppingList[];
  lastModified: string;
}

// API Request types
export interface CreateProductRequest {
  product: Omit<Product, 'priceHistory'>;
}

export interface UpdateProductRequest {
  productName: string;
  updates: Partial<Product>;
}

export interface CreateShoppingListRequest {
  list: Omit<ShoppingList, 'createdAt' | 'updatedAt'>;
}

export interface UpdateShoppingListRequest {
  listName: string;
  updates: Partial<ShoppingList>;
}

export interface AddToShoppingListRequest {
  listName: string;
  productName: string;
  quantity?: number;
  quantityUnit?: 'pcs' | 'kg' | 'g' | 'l' | 'ml';
  unitPrice?: number;
  store?: string;
}

export interface GlobalPriceHistoryEntry {
  id: string;
  productName: string;
  store: string;
  storeName?: string;
  oldPrice?: number;
  newPrice: number;
  timestamp: string;
  updatedBy: 'user' | 'photo-ai' | 'manual';
  confidence?: number;
}

export interface GetPriceHistoryRequest {
  productName?: string;
  store?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
}

export interface PhotoAnalysisResult {
  extractedText: string[];
  detectedPrice?: number;
  suggestedProductName?: string;
  confidence: number;
  rawOcrResult: any;
  aiAnalysis?: {
    productName?: string;
    completedProductName?: string;
    storeName?: string;
    category?: string;
    confidence: number;
  };
}

export interface CreateProductFromPhotoRequest {
  imageData: string;
  store: string;
  userLocation?: {
    latitude: number;
    longitude: number;
  };
}

// UI State types
export interface AppState {
  user: User | null;
  products: Product[];
  shoppingLists: ShoppingList[];
  currentList: ShoppingList | null;
  loading: boolean;
  error: string | null;
}

export interface ProductFilters {
  search: string;
  store: string;
  favoritesOnly: boolean;
  sortBy: 'name' | 'price' | 'store';
}

// Constants
export const CATEGORIES = [
  'Maitotuotteet',
  'Lihatuotteet', 
  'Kalatuotteet',
  'Hedelmät',
  'Vihannekset',
  'Leivonnaiset',
  'Juomat',
  'Pakasteet',
  'Kuivatuotteet',
  'Hygieniatuotteet',
  'Siivoustuotteet',
  'Muu'
] as const;
export type Category = typeof CATEGORIES[number];

export const SORT_OPTIONS = ['name', 'price', 'store'] as const;
export type SortOption = typeof SORT_OPTIONS[number]; 